import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row, Col, Tag, Typography, Button, Tooltip,
} from 'antd';
import {
  PlayCircleOutlined, PauseCircleOutlined,
  StepBackwardOutlined, StepForwardOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import { VIDEO_DEMOS, type VideoDemo } from '../content/videoContent';
import { atriumTokens } from '../../../theme/theme';

const { Text } = Typography;

// ─── Animated Player ──────────────────────────────────────────────────────────

const SLIDE_DURATION_MS = 4500; // each slide shows for 4.5 seconds

// WCAG 2.3.3 / 95.5-autoplay: honour the user's reduced-motion preference.
const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const toTimeStr = (totalSlides: number, currentSlide: number, durationStr: string): string => {
  const [mStr, sStr] = durationStr.split(':');
  const totalSec = parseInt(mStr, 10) * 60 + parseInt(sStr, 10);
  const elapsed = Math.round((currentSlide / Math.max(totalSlides - 1, 1)) * totalSec);
  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

interface AnimatedPlayerProps {
  video: VideoDemo;
}

const AnimatedPlayer: React.FC<AnimatedPlayerProps> = ({ video }) => {
  const [current, setCurrent] = useState(0);
  // 95.5-autoplay: never auto-start playback when the user prefers reduced motion.
  const reducedMotion = prefersReducedMotion();
  const [playing, setPlaying] = useState(false);
  const [visible, setVisible] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const total = video.slides.length;

  const goTo = useCallback((idx: number) => {
    const clamped = Math.max(0, Math.min(idx, total - 1));
    if (reducedMotion) {
      // Skip the decorative fade — jump straight to the target slide.
      setCurrent(clamped);
      setVisible(true);
      return;
    }
    setVisible(false);
    setTimeout(() => {
      setCurrent(clamped);
      setVisible(true);
    }, 250);
  }, [total, reducedMotion]);

  // Properly advance with fade (fade skipped under reduced-motion)
  const advance = useCallback(() => {
    if (reducedMotion) {
      setCurrent(prev => (prev + 1) % total);
      setVisible(true);
      return;
    }
    setVisible(false);
    setTimeout(() => {
      setCurrent(prev => (prev + 1) % total);
      setVisible(true);
    }, 220);
  }, [total, reducedMotion]);

  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(advance, SLIDE_DURATION_MS);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [playing, advance]);

  const pct = total > 1 ? (current / (total - 1)) * 100 : 0;
  const timeStr = toTimeStr(total, current, video.duration);
  const slide = video.slides[current];

  return (
    <div style={{ background: atriumTokens.card, borderRadius: atriumTokens.radiusCard, overflow: 'hidden', border: `1px solid ${atriumTokens.line}`, boxShadow: atriumTokens.sh2, fontFamily: atriumTokens.fontFamily }}>
      {/* Player chrome - top bar */}
      <div style={{
        background: atriumTokens.card, borderBottom: `1px solid ${atriumTokens.line}`, padding: '10px 16px',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <VideoCameraOutlined style={{ color: video.accentColor, fontSize: 16 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ color: atriumTokens.ink, fontWeight: 600, fontSize: 13, display: 'block' }}>{video.title}</Text>
          <Text style={{ color: atriumTokens.mut, fontSize: 11 }}>
            Slide {current + 1} of {total} · {slide.heading}
          </Text>
        </div>
        <Tag style={{ borderColor: `${video.accentColor}60`, color: video.accentColor, background: `${video.accentColor}15`, fontSize: 11, margin: 0 }}>
          {video.duration}
        </Tag>
      </div>

      {/* Slide viewport */}
      <div style={{
        height: 340, overflow: 'hidden', position: 'relative',
        opacity: visible ? 1 : 0,
        transition: reducedMotion ? 'none' : 'opacity 0.22s ease',
      }}>
        {slide.visual}
      </div>

      {/* Controls bar */}
      <div style={{ background: atriumTokens.card, borderTop: `1px solid ${atriumTokens.line}`, padding: '10px 16px' }}>
        {/* Progress / seek bar */}
        <div
          role="slider"
          tabIndex={0}
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={total - 1}
          aria-valuenow={current}
          aria-valuetext={`${timeStr} of ${video.duration}, slide ${current + 1} of ${total}`}
          style={{ height: 4, background: atriumTokens.line, borderRadius: 2, marginBottom: 10, cursor: 'pointer', outline: 'revert' }}
          onClick={e => {
            const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
            const ratio = (e.clientX - rect.left) / rect.width;
            goTo(Math.round(ratio * (total - 1)));
          }}
          onKeyDown={e => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
              e.preventDefault();
              goTo(current - 1);
            } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
              e.preventDefault();
              goTo(current + 1);
            } else if (e.key === 'Home') {
              e.preventDefault();
              goTo(0);
            } else if (e.key === 'End') {
              e.preventDefault();
              goTo(total - 1);
            }
          }}
        >
          <div style={{
            height: '100%', width: `${pct}%`,
            background: `linear-gradient(90deg, ${video.accentColor}, ${video.accentColor}bb)`,
            borderRadius: 2, transition: 'width 0.3s',
          }} />
        </div>

        {/* Control buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Tooltip title="Previous slide">
            <Button
              type="text" size="small"
              aria-label="Previous slide"
              icon={<StepBackwardOutlined style={{ color: current > 0 ? atriumTokens.ink2 : atriumTokens.mut2 }} />}
              onClick={() => goTo(current - 1)}
              disabled={current === 0}
              style={{ padding: '0 6px' }}
            />
          </Tooltip>
          <Button
            type="text" size="small"
            aria-label={playing ? 'Pause' : 'Play'}
            icon={playing
              ? <PauseCircleOutlined style={{ color: video.accentColor, fontSize: 22 }} />
              : <PlayCircleOutlined style={{ color: video.accentColor, fontSize: 22 }} />}
            onClick={() => setPlaying(p => !p)}
            style={{ padding: '0 4px' }}
          />
          <Tooltip title="Next slide">
            <Button
              type="text" size="small"
              aria-label="Next slide"
              icon={<StepForwardOutlined style={{ color: current < total - 1 ? atriumTokens.ink2 : atriumTokens.mut2 }} />}
              onClick={() => goTo(current + 1)}
              disabled={current === total - 1}
              style={{ padding: '0 6px' }}
            />
          </Tooltip>

          <Text style={{ color: atriumTokens.mut, fontSize: 11, marginLeft: 6 }}>
            {timeStr} / {video.duration}
          </Text>

          <div style={{ flex: 1 }} />

          {/* Chapter dots */}
          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            {video.slides.map((_, i) => (
              <Tooltip key={i} title={video.slides[i].heading}>
                <button
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`Jump to chapter: ${video.slides[i].heading}`}
                  aria-current={i === current ? 'true' : undefined}
                  style={{
                    width: i === current ? 20 : 7, height: 7, borderRadius: 4,
                    background: i === current ? video.accentColor : atriumTokens.line,
                    cursor: 'pointer', transition: 'all 0.25s',
                    border: 'none', padding: 0, display: 'block',
                  }}
                />
              </Tooltip>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Video Card (thumbnail) ───────────────────────────────────────────────────

interface VideoCardProps {
  video: VideoDemo;
  onSelect: (v: VideoDemo) => void;
  isActive: boolean;
}

const VideoCard: React.FC<VideoCardProps> = ({ video, onSelect, isActive }) => (
  <button
    type="button"
    onClick={() => onSelect(video)}
    aria-label={`Play tutorial: ${video.title}`}
    aria-pressed={isActive}
    style={{
      borderRadius: atriumTokens.radiusControl, overflow: 'hidden', cursor: 'pointer',
      border: isActive ? `2px solid ${video.accentColor}` : `2px solid ${atriumTokens.line}`,
      boxShadow: isActive ? `0 0 0 3px ${video.accentColor}25` : atriumTokens.sh,
      transition: 'all 0.2s', fontFamily: atriumTokens.fontFamily,
      padding: 0, background: 'transparent', textAlign: 'left', width: '100%', display: 'block',
    }}
  >
    {/* Thumbnail */}
    <div style={{
      height: 90, background: `linear-gradient(135deg, ${video.accentColor}20 0%, ${video.accentColor}08 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 6,
      position: 'relative',
    }}>
      <div style={{ fontSize: 28, color: video.accentColor }}>{video.icon}</div>
      <div style={{
        position: 'absolute', bottom: 6, right: 8,
        background: 'rgba(0,0,0,0.6)', borderRadius: 3,
        padding: '1px 5px', fontSize: 10, color: '#fff', fontWeight: 600,
      }}>
        {video.duration}
      </div>
      {isActive && (
        <div style={{
          position: 'absolute', top: 6, left: 6,
          background: video.accentColor, borderRadius: 3,
          padding: '1px 6px', fontSize: 9, color: '#fff', fontWeight: 700,
        }}>
          NOW PLAYING
        </div>
      )}
    </div>
    {/* Info */}
    <div style={{ padding: '8px 10px', background: atriumTokens.card }}>
      <Text strong style={{ fontSize: 12, display: 'block', lineHeight: 1.3, color: atriumTokens.ink }}>{video.title}</Text>
      <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 2, lineHeight: 1.3 }}>
        {video.slides.length} slides · {video.category}
      </Text>
    </div>
  </button>
);

// ─── Main VideoTutorials Component ───────────────────────────────────────────

export const VideoTutorials: React.FC = () => {
  const { user } = useAuthStore();
  const role = user?.role ?? 'admin';

  const roleVideos = VIDEO_DEMOS.filter(v => v.roles.includes(role));
  const [activeVideo, setActiveVideo] = useState<VideoDemo>(roleVideos[0] ?? VIDEO_DEMOS[0]);

  if (roleVideos.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0', color: atriumTokens.mut, fontFamily: atriumTokens.fontFamily }}>
        <VideoCameraOutlined style={{ fontSize: 48, marginBottom: 12, color: atriumTokens.line2 }} />
        <div>No video tutorials available for your role yet.</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 0', fontFamily: atriumTokens.fontFamily }}>
      <div style={{
        background: atriumTokens.blueS2, border: `1px solid ${atriumTokens.line}`, borderRadius: atriumTokens.radiusControl,
        padding: '10px 14px', marginBottom: 20, fontSize: 13, color: atriumTokens.blueInk,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <VideoCameraOutlined />
        <span>
          <strong>Interactive demos</strong> — press Play to auto-advance slides, or click
          the chapter dots to jump to any step. Each demo walks you through a key feature.
        </span>
      </div>

      <Row gutter={[20, 20]}>
        {/* Left: video list */}
        <Col xs={24} md={8}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {roleVideos.map(v => (
              <VideoCard
                key={v.id}
                video={v}
                onSelect={setActiveVideo}
                isActive={activeVideo.id === v.id}
              />
            ))}
          </div>
        </Col>

        {/* Right: player */}
        <Col xs={24} md={16}>
          <AnimatedPlayer key={activeVideo.id} video={activeVideo} />

          {/* Slide index below player */}
          <div style={{ marginTop: 14 }}>
            <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8, color: atriumTokens.ink }}>Chapters</Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {activeVideo.slides.map((s, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 10px', borderRadius: atriumTokens.radiusControlSm, background: atriumTokens.bg,
                  border: `1px solid ${atriumTokens.line}`,
                }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    background: activeVideo.accentColor + '20',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 700, color: activeVideo.accentColor,
                  }}>{i + 1}</div>
                  <Text style={{ fontSize: 12 }}>{s.heading}</Text>
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};
